import React, { useContext, useEffect, useState } from 'react';
import envConfig from '@nile/home/src/Config/env';
import {
  getAccessor,
  getDefaultValue,
  getType,
  isEditable
} from '@nile/reports/src/modules/charts/utils/list-helpers';
import {
  AppContext,
  ContentDeciderContainer,
  SuperTable,
  SkeletonTableLoading,
  getTranslatedText,
  saveTableRowCount,
  getColumnWidth as superTableColumnWidth,
  ALL_TABLES,
  getTableRowCount,
  getAllColumnWidth,
  saveColumnWidth,
  useProduct
} from '@nile/components';
import {
  AvatarCell,
  SiteCell,
  ClientCell
} from '@nile/reports/src/modules/reports/components/subpages/HistoryTable/Cell';
import { DateTimeCell } from '@nile/mangifera/src/Components/Task';
import { GET_DOWNLOAD_INVENTORY_LIST } from '@nile/reports/src/modules/reports/graphql';
import { AccountClient } from '@nile/graphql';
import LinkCell from '@nile/reports/src/modules/charts/utils/list-helpers/LinkCell';
import DownloadCell from '@nile/reports/src/modules/charts/utils/list-helpers/DownloadCell';
import CreatedByCell from '@nile/reports/src/modules/charts/utils/list-helpers/CreatedByCell';
import { useIntl } from 'react-intl';
import DeleteActionCell from '@nile/rmm/src/snmp/custom-asset-monitors-config-list/DeleteActionCell.tsx';

function HistoryTable(props) {
  const { data, actionClickDelete, context, enableDeleteAction } = props;
  const intl = useIntl();
  const isMSPProduct = useProduct('MSP');
  const appContext = useContext(AppContext);

  const { user } = appContext;
  const tableName = ALL_TABLES.pdfHistory;
  const columnWidthObject = getAllColumnWidth(tableName);
  const ROW_COUNT = getTableRowCount(tableName);
  const [rowCount, setRowCount] = useState(ROW_COUNT);

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'name', columns: [] }
  ]);
  const [getData, setData] = useState([]);
  const historyListRef = React.useRef(null);
  historyListRef.current = getData;
  const [listInfo, setListInfo] = useState(null);

  const options = {
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'triggeredTime',
        order: 'DESC'
      }
    ]
  };

  const getActions = (col) => {
    switch (col.name) {
      case 'status':
        return {
          onClick: () => console.log('test--->')
        };
      case 'deleteAction':
        return {
          delete: (e) => {
            actionClickDelete(e, fetchViewData);
          }
        };
      default:
        return {};
    }
  };

  const fetchViewData = async (personalization, pageOptions) => {
    const requestOption = {
      module: 'REPORT',
      moduleSpecificId: data?.reportId,
      listInfo: pageOptions
        ? {
            page: pageOptions.page || listInfo?.page || options.page,
            pageSize:
              pageOptions.pageSize || listInfo?.pageSize || options.pageSize,
            sort: pageOptions.sort || null
          }
        : {
            ...options
          }
    };
    await AccountClient.query({
      query: GET_DOWNLOAD_INVENTORY_LIST,
      variables: requestOption,
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data) {
        setData(res?.data?.getDownloadInventoryList?.inventoryList);
        setListInfo(res?.data?.getDownloadInventoryList?.listInfo);
      }
    });
    const cols = [
      {
        name: 'user',
        label: getTranslatedText(intl, 'communityScript.createdBy'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        size: 250,
        disableSortBy: true,
        disableFilters: true
      },
      {
        name: 'triggeredSource',
        label: getTranslatedText(intl, 'attribute.source'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: true,
        disableFilters: true
      },
      {
        name: 'triggeredTime',
        label: getTranslatedText(intl, 'common.created.on'),
        disableFilters: true
      },
      {
        name: 'status',
        label: getTranslatedText(intl, 'common.status'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: true,
        disableFilters: true
      }
    ];
    if (isMSPProduct) {
      cols.splice(
        2,
        0,
        {
          name: 'client.name',
          label: getTranslatedText(
            intl,
            'client.navigation.quotes.list.client'
          ),
          dataType: 'string',
          uiDataType: 'text',
          filterType: 'text',
          disableSortBy: true,
          disableFilters: true
        },
        {
          name: 'site',
          label: getTranslatedText(intl, 'app.common.site'),
          dataType: 'string',
          uiDataType: 'text',
          filterType: 'text',
          disableSortBy: true,
          disableFilters: true
        }
      );
    }
    enableDeleteAction &&
      cols.push({
        name: 'deleteAction',
        disableSortBy: true,
        disableFilters: true
      });
    const columns = cols.map((col, idx) => {
      return {
        header: col.label,
        accessorKey: col.name,
        size: superTableColumnWidth(columnWidthObject, col.name, col?.size),
        minSize: 50,
        Header: col.label,
        accessor: getAccessor(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        disableFilters: col?.disableFilters,
        disableSortBy: col?.disableSortBy,
        helpers: {
          getDefaultValue
        },
        width: col.width || 230,
        actions: getActions(col),
        field: col,
        meta: {
          custom: true,
          title: 'reports.history.delete.confirmation.heading',
          description: 'reports.history.delete.confirmation.description',
          fileType: data?.reportType === 'PDF' ? data?.reportType : 'CSV'
        }
      };
    });
    setColumnGroup([{ Header: 'Name', header: 'name', columns }]);
  };

  const defaultColumnCells = {
    Link: LinkCell,
    CreatedBy: CreatedByCell,
    Name: AvatarCell,
    DateTime: DateTimeCell,
    Status: DownloadCell,
    Site: SiteCell,
    DeleteAction: DeleteActionCell,
    Client: ClientCell
  };

  useEffect(() => {
    fetchViewData();
  }, []);

  return (
    <div className="vh-set">
      <ContentDeciderContainer
        isLoading={!columnGroup[0]?.columns?.length}
        hasContent
        mainContent={
          <>
            <SuperTable
              padded="sm"
              noBulkSelect={enableDeleteAction ? false : true}
              context={context || {}}
              columns={columnGroup}
              data={historyListRef.current}
              setData={setData}
              rowCount={rowCount}
              refreshTable={fetchViewData}
              listInfo={listInfo}
              defaultColumnCells={defaultColumnCells}
              setRowCount={setRowCount}
              saveTableRowCount={saveTableRowCount}
              saveColumnWidth={saveColumnWidth}
              tableName={tableName}
              columnWidthPersonalization
              user={user}
            />
          </>
        }
        fullHeight
        loadingContent={<SkeletonTableLoading />}
        notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
      />
    </div>
  );
}

export default HistoryTable;
